import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { MapPin, Mail, Phone, Clock, Send, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createContact } from "@/lib/api";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import RecaptchaWrapper from "@/components/RecaptchaWrapper";

function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;
  const { verifyRecaptcha, isReady: recaptchaReady } = useRecaptcha();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Verify reCAPTCHA first
      const recaptchaToken = await verifyRecaptcha("contact_form");
      
      await createContact({
        ...formData,
        recaptcha_token: recaptchaToken
      });
      toast.success("Bericht succesvol verzonden! We nemen zo snel mogelijk contact met je op.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Kon bericht niet verzenden. Probeer het opnieuw.");
      console.error("Error sending contact:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-3xl md:text-4xl font-black font-['Chivo'] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>NEEM CONTACT OP</h1>
          <p className={`max-w-2xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Heb je een vraag of wil je lid worden van onze club? We horen graag van je. Stuur ons een bericht en we reageren zo snel mogelijk.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card className={isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-600/20' : 'bg-blue-50'}`}>
                    <MapPin className={`h-6 w-6 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Adres</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      Sporthal Gent<br />
                      Ter Linden 29<br />
                      9052 Gent, Belgium
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-600/20' : 'bg-blue-50'}`}>
                    <Mail className={`h-6 w-6 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Email</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>info@futsalgent.be</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-600/20' : 'bg-blue-50'}`}>
                    <Phone className={`h-6 w-6 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Telefoon</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>+32 477 98 70 47</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-600/20' : 'bg-blue-50'}`}>
                    <Clock className={`h-6 w-6 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Openingstijden</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      Ma - Vr: 09:00 - 18:00<br />
                      Za: 10:00 - 14:00<br />
                      Zo: Gesloten
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className={`lg:col-span-2 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <CardHeader>
              <CardTitle className={`font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>Stuur ons een bericht</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Naam</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={isDark ? 'bg-slate-800 border-white/10 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}
                      placeholder="Uw naam"
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={isDark ? 'bg-slate-800 border-white/10 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}
                      placeholder="uw@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Onderwerp</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={isDark ? 'bg-slate-800 border-white/10 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}
                    placeholder="Waar gaat het over?"
                    data-testid="contact-subject"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Bericht</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className={`resize-none ${isDark ? 'bg-slate-800 border-white/10 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                    placeholder="Uw bericht..."
                    data-testid="contact-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)] btn-primary"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    "Verzenden..."
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Bericht Versturen
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function Contact() {
  return (
    <RecaptchaWrapper>
      <ContactPage />
    </RecaptchaWrapper>
  );
}
